import React, { useMemo } from 'react';

import {
  CAMPAIGN_INFO_TYPES,
  type ICampaignInfo,
  type IRebateResources,
  normalizeCampaignAssets,
  REBATE_CAMPAIGN_TYPE,
  useCampaignCardScale,
  useCampaignCardStyles,
  useCampaignDataAttributes,
  useCampaignState,
} from 'features/campaigns';
import BottomCardInfoCampaign from '../../../shared/BottomCardInfo/BottomCardrInfoCampaign';
import CampaignInfoDisplay from '../../../shared/BottomCardInfo/components/CampaignInfoDisplay';
import Image from 'shared/components/Image';
import { useTrueMobile } from 'shared/hooks/useTrueMobile';
import { RebateSubtitleCampaign } from '../shared';

import { SINGLE_VIEW_MODE } from '../../../../../../constants';
import rebateVariables from '../../../../styles/variants/rebate-variables.module.css';
import type { CampaignCardStyles } from '../../../shared/CampaignCard/CampaignCardStyles';
import originalHasManyStyles from '../../../shared/CampaignCard/hasManyStyles.module.css';
import rebateCardStyles from './RebateCard.module.css';

import RebateProgressBar from './components/RebateProgressBar/RebateProgressBar';

type RebateCardProps = Omit<ICampaignInfo, 'component'> & {
  resources: IRebateResources;
};

const RebateCard: React.FC<RebateCardProps> = ({ id, name, resources, prizeFund, baseCurrency, campaignId }) => {
  const { isCollapsed, collapsedAttributes, getCollapsedClassName, viewMode } = useCampaignState();
  const isSingleView = viewMode === SINGLE_VIEW_MODE;
  const { getSharedCardStyles } = useCampaignCardStyles();
  const dataAttributes = useCampaignDataAttributes({
    additionalProps: { 'data-variant': 'card' },
  });
  const hasMany = dataAttributes['data-has-many'];
  const isMobile = useTrueMobile();

  const sharedCardStyles = getSharedCardStyles();
  const hasManyStyles = originalHasManyStyles as unknown as CampaignCardStyles;

  const getMaxWidth = () => {
    if (!isMobile) return isCollapsed ? 370 : 445;
    if (isCollapsed) return 280;
    return hasMany ? 320 : 350;
  };

  const { titleRef, scale } = useCampaignCardScale(name, {
    maxWidth: getMaxWidth(),
    recalculateKey: isCollapsed,
  });

  const normalizedAssets = useMemo(() => normalizeCampaignAssets(resources, REBATE_CAMPAIGN_TYPE), [resources]);

  return (
    <div
      className={`${sharedCardStyles.campaignCardContainer} ${hasMany ? hasManyStyles.campaignCardContainer : ''} ${rebateCardStyles.campaignCardContainer} ${rebateVariables['rebateTheme']}`}
      {...collapsedAttributes}
      {...dataAttributes}
    >
      <div
        className={`${sharedCardStyles.topHeaderContainerCollapsed} ${hasMany ? hasManyStyles.topHeaderContainerCollapsed : ''}`}
        {...dataAttributes}
      >
        {/* Icon: always in DOM, CSS toggles display via data-collapsed on parent */}
        <Image
          src={normalizedAssets.icons.logo}
          className={`${sharedCardStyles.campaignIcon} ${sharedCardStyles.collapsedOnly}`}
        />

        <div
          className={`${sharedCardStyles.namePrizeWrapper} ${hasMany ? hasManyStyles.namePrizeWrapper : ''} ${rebateCardStyles.namePrizeWrapper}`}
          {...dataAttributes}
        >
          <div
            className={`${getCollapsedClassName(
              sharedCardStyles.campaignTitleContainer,
              sharedCardStyles.campaignTitleCollapsed,
            )} ${hasMany ? hasManyStyles.campaignTitleContainer : ''} 
            ${getCollapsedClassName(rebateCardStyles.campaignTitleContainer, rebateCardStyles.campaignTitleCollapsed)}`}
            {...(isCollapsed ? collapsedAttributes : {})}
            {...dataAttributes}
          >
            <span
              className={sharedCardStyles.campaignNameText}
              ref={titleRef as React.RefObject<HTMLSpanElement>}
              style={{
                transform: scale < 1 ? `scale(${scale})` : undefined,
              }}
            >
              {name}
            </span>
            {/* Rebate cards don't cycle an info type with the name in the collapsed
                header (no textCycle animation), so there is deliberately no second
                CampaignInfoDisplay child here — the name renders statically. */}
          </div>
          <RebateSubtitleCampaign content={prizeFund} currency={baseCurrency} variant="card" />
        </div>

        {/* Timer: always in DOM, CSS toggles display via data-collapsed on parent */}
        <div className={sharedCardStyles.collapsedOnly}>
          <CampaignInfoDisplay infoTypes={[CAMPAIGN_INFO_TYPES.TIMER]} campaignId={campaignId} />
        </div>
      </div>

      { isSingleView && 
        <div
          className={`${rebateCardStyles.progressBarWrapper} ${sharedCardStyles.expandedOnly}`}
          {...dataAttributes}
        >
          <RebateProgressBar campaignId={id} />
        </div>
      }

      {/* Separator + bottom info: always in DOM, hidden when collapsed */}
      <div
        className={`${sharedCardStyles.separatorAndInfoContainer} ${sharedCardStyles.expandedOnly}`}
        {...dataAttributes}
      >
        <div
          className={`${sharedCardStyles.separator} ${hasMany ? hasManyStyles.separator : ''} ${rebateCardStyles.separator}`}
          {...dataAttributes}
        />
        <BottomCardInfoCampaign campaignId={campaignId} />
      </div>
    </div>
  );
};

export default RebateCard;
